import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Menu,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as UploadIcon,
  Share as ShareIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { portalAdminApi, AdminPortalDokument } from '../services/portalService';

const PortalDokumenteAdmin: React.FC = () => {
  const navigate = useNavigate();
  const [dokumente, setDokumente] = useState<AdminPortalDokument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterTyp, setFilterTyp] = useState<string>('');
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedDokumentId, setSelectedDokumentId] = useState<number | null>(null);

  useEffect(() => {
    loadDokumente();
  }, [filterStatus, filterTyp]);

  const loadDokumente = async () => {
    try {
      setLoading(true);
      setError(null);
      const options: { status?: string; nurHochgeladen?: boolean } = {};
      if (filterStatus) options.status = filterStatus;
      if (filterTyp === 'hochgeladen') options.nurHochgeladen = true;

      const response = await portalAdminApi.getAlleDokumente(options);
      setDokumente(response.data);
    } catch (err: any) {
      console.error('Fehler beim Laden der Portal-Dokumente:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der Portal-Dokumente');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (dok: AdminPortalDokument) => {
    try {
      const response = await portalAdminApi.downloadDokument(dok.portalDokumentID);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', dok.dateiName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  const handleStatusChange = async (id: number, newStatus: string) => {
    try {
      await portalAdminApi.updateDokumentStatus(id, newStatus);
      setDokumente(dokumente.map(d =>
        d.portalDokumentID === id ? { ...d, status: newStatus } : d
      ));
      setStatusMenuAnchor(null);
    } catch (err) {
      console.error('Fehler beim Ändern des Status:', err);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Neu':
        return 'warning';
      case 'Bearbeitet':
        return 'success';
      case 'Archiviert':
        return 'default';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Portal-Dokumente</Typography>
        <Box display="flex" gap={2}>
          <Tooltip title="Aktualisieren">
            <IconButton onClick={loadDokumente} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box display="flex" gap={2} mb={3}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filterStatus}
            label="Status"
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <MenuItem value="">Alle</MenuItem>
            <MenuItem value="Neu">Neu</MenuItem>
            <MenuItem value="Bearbeitet">Bearbeitet</MenuItem>
            <MenuItem value="Archiviert">Archiviert</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Typ</InputLabel>
          <Select
            value={filterTyp}
            label="Typ"
            onChange={(e) => setFilterTyp(e.target.value)}
          >
            <MenuItem value="">Alle</MenuItem>
            <MenuItem value="hochgeladen">Vom Portal hochgeladen</MenuItem>
            <MenuItem value="freigegeben">Freigegeben</MenuItem>
          </Select>
        </FormControl>
        {(filterStatus || filterTyp) && (
          <Button
            variant="outlined"
            onClick={() => {
              setFilterStatus('');
              setFilterTyp('');
            }}
          >
            Filter zurücksetzen
          </Button>
        )}
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Typ</TableCell>
              <TableCell>Titel</TableCell>
              <TableCell>Mandant</TableCell>
              <TableCell>Kategorie</TableCell>
              <TableCell>Datei</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Datum</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dokumente.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="textSecondary" sx={{ py: 3 }}>
                    Keine Portal-Dokumente vorhanden
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              dokumente.map((dok) => (
                <TableRow key={dok.portalDokumentID} hover>
                  <TableCell>
                    <Chip
                      icon={dok.vomPortalHochgeladen ? <UploadIcon /> : <ShareIcon />}
                      label={dok.vomPortalHochgeladen ? 'Hochgeladen' : 'Freigegeben'}
                      size="small"
                      color={dok.vomPortalHochgeladen ? 'info' : 'success'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {dok.titel}
                    </Typography>
                    {dok.beschreibung && (
                      <Typography variant="caption" color="textSecondary">
                        {dok.beschreibung}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ cursor: 'pointer', color: 'primary.main' }}
                      onClick={() => navigate(`/adressen?id=${dok.adresseID}`)}
                    >
                      {dok.adresseName || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={dok.kategorie} size="small" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{dok.dateiName}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {formatFileSize(dok.dateiGroesse)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={dok.status}
                      size="small"
                      color={getStatusColor(dok.status)}
                      onClick={(e) => {
                        setStatusMenuAnchor(e.currentTarget);
                        setSelectedDokumentId(dok.portalDokumentID);
                      }}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {dok.vomPortalHochgeladen
                        ? formatDate(dok.hochgeladenAm)
                        : formatDate(dok.freigegebenAm)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" justifyContent="flex-end" gap={1}>
                      <Tooltip title="Herunterladen">
                        <IconButton size="small" onClick={() => handleDownload(dok)}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Als bearbeitet markieren">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleStatusChange(dok.portalDokumentID, 'Bearbeitet')}
                          disabled={dok.status === 'Bearbeitet'}
                        >
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Mandant anzeigen">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/adressen?id=${dok.adresseID}`)}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Status-Änderungs-Menü */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={() => setStatusMenuAnchor(null)}
      >
        <MenuItem onClick={() => selectedDokumentId && handleStatusChange(selectedDokumentId, 'Neu')}>
          Neu
        </MenuItem>
        <MenuItem onClick={() => selectedDokumentId && handleStatusChange(selectedDokumentId, 'Bearbeitet')}>
          Bearbeitet
        </MenuItem>
        <MenuItem onClick={() => selectedDokumentId && handleStatusChange(selectedDokumentId, 'Archiviert')}>
          Archiviert
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default PortalDokumenteAdmin;
